import { IAttrValue, IRowData } from "@multiweb/types/contracts";
import { Code } from "../../../../Global/Base/Utils/EventUtils";
import { StringUtils } from "../../../../Global/Base/Utils/StringUtils";
import { OWObject } from "../../../../OW.Remoting/Remoting/DataTypes/OWObject";
import { AwaitableEvent } from "../../../UI.BaseControls/AwaitableEvent";
import { EventHelper } from "../../../UI.BaseControls/Helpers/EventHelper";
import { createElement, registerCustomElement } from "../../../UI.ElementarControls/Utils/CustomElementUtils";
import { ROW_SELECTION_KEY_CODES } from "../../DataGrid";
import { EditableDataGrid } from "../../EditableDatagrid";
import { ICellValueChangedPrewAwaitableEventData } from "../../IEvent";
import { Column } from "../Columns/Column";
import { IEditableListDataRowElement } from "./IDataRow";
import { ListDataRow } from "./ListDataRow";
import { TableCell } from "./TableCell";

const cellInEditListenerKey = "cellInEditListener";

export class EditableDataRow extends ListDataRow implements IEditableListDataRowElement {
    protected override _dataGrid: EditableDataGrid<EditableDataRow>;

    /**
     * Jestli je radek v editaci
     */
    private _isEdited: boolean;
    private _isChanged: boolean;

    /**
     * atribut, ktery dostane focus
     */
    private _focusAttribute: string = null;

    public get focusAttribute(): string | null {
        if (this.checkAndLogDisposedUsage("get focusAttribute")) return this._focusAttribute;
        return this._focusAttribute;
    }
    public set focusAttribute(v: string | null) {
        if (this.checkAndLogDisposedUsage("set focusAttribute")) return;
        this._focusAttribute = v;
    }

    //#region Properties
    /**
     * Informace o tom, zda byl objekt zmeněn a je tedy v kolekci rozeditovaných záznamů
     */
    public get isChanged(): boolean {
        if (this.checkAndLogDisposedUsage("get isChanged")) return false;

        return this._isChanged;
    }

    public set isChanged(v: boolean) {
        if (this.checkAndLogDisposedUsage("set isChanged")) return;

        if (v === this.isChanged) {
            return;
        }

        this._isChanged = v;
        this.classList.toggle("IsChanged", v);

        if (v) {
            this._dataGrid.editedRows.push(this);
            this.visible = true;
            this._dataGrid.incEditedRows();
        } else {
            this.isInEdit = false;
            const index = this._dataGrid.editedRows.indexOf(this);
            if (index < 0)
                return;
            this._dataGrid.editedRows.splice(index, 1);
            this._dataGrid.decEditedRows();
            if (this._dataGrid.displayedEditedRows) {
                if (this.isSelected) {
                    this.isSelected = false;
                    this._dataGrid.dispatchSelectionChanged();
                }
                this.visible = false;
            }
        }
    }

    /**
     * Informace o tom, zda je radek v editaci
     */
    public get isInEdit(): boolean {
        if (this.checkAndLogDisposedUsage("get isInEdit")) return false;

        return this._isEdited;
    }

    public set isInEdit(v: boolean) {
        if (this.checkAndLogDisposedUsage("set isInEdit")) return;

        const oV = this._isEdited;
        this._isEdited = v;
        if (oV !== v) {
            this._toEdit(v);
        } else if (v) {
            this._focusCell();
        }
    }
    //#endregion Properties

    //#region Methods
    protected override _constructor(): void {
        super._constructor();

        this._isEdited = false;
    }

    public setChangedData(attrName: string, value: IAttrValue, isChange: boolean): Promise<void> {
        if (this.checkAndLogDisposedUsage("setChangedData")) {
            return Promise.resolve();
        }

        this.isChanged = isChange;
        return this.setData(attrName, value);
    }

    /**
     * Nastavení novou instanci pro buňky v editaci (LOVEdit a FileEdit)
     * @param obj - nově načtený object pro řádek
     */
    public setInstance(obj: OWObject): void {
        if (this.checkAndLogDisposedUsage("setInstance")) return;

        !obj.isDisposed && obj.disposed.add(this._instanceDisposedListener.bind(this));

        for (const cell of this.dataCells) {
            cell.setInstance(obj);
        }
    }

    protected override _initCells(dataRow: IRowData, frozenCount?: number, columns?: Column[]): void {
        super._initCells(dataRow, frozenCount, columns);
        this._toEdit(this.isInEdit);
    }

    /**
     * Posluchač na uvolnění instance.
     *
     * Slouží k odstranění `instanceId` z atributů EditableDataRow elementu při uvolnění instance.
     * @param instance Instance.
     * @private
     */
    private _instanceDisposedListener(instance: OWObject): void {
        if (this.checkAndLogDisposedUsage("_instanceDisposedListener")) return;

        const instanceId = this.getAttribute("data-instanceid");
        if (instanceId && instanceId === instance?.instanceID) {
            this.removeAttribute("data-instanceid");
        }
    }

    private async _cellValueChanged(cell: TableCell, value: IAttrValue): Promise<void> {
        if (this.checkAndLogDisposedUsage("_cellValueChanged")) return;

        // událost umožňuje zrušit změnu hodnoty
        const eventData = new AwaitableEvent("CellValueChangedPreview", true, {
            bubbles: true,
            detail: { attrName: cell.column.attrName, value: value } as ICellValueChangedPrewAwaitableEventData,
        });
        this.dispatchEvent(eventData);
        if (!(await eventData.resultAwaiter)) { // pokud je výsledkem preview false, tak se musí změna hodnoty zrušit
            cell.dataSet = cell.dataSet; // do komponenty v buňce se znovu nastaví původní hodnota (v dataSetu buňky je pořád ta původní hodnota - změněná je zatím jen v komponentě)
            return;
        }

        this.isChanged = true;

        if (this.dataKey) {
            const editData: { [v: string]: IAttrValue } = {};
            editData[cell.column.attrName] = value;

            await this._dataGrid.dataSource.setValues(this.dataKey, editData);
        } else {
            throw Error("editObject not valid");
        }
    }

    private _focusCell(): void {
        this.dataCells.forEach(cell => {
            if (cell.isFocused) {
                cell.blur();
            }
        });

        let cellFocused = false;
        if (this._focusAttribute) {
            // If _focusAttributeId is present, it means editing by clicking into the cell.
            // Focus the cell with the given attribute id and clear the _focusAttributeId.
            const cell = this.dataCells.find(
                (cell) =>
                    cell.column.visible &&
                    cell.column.isEditable &&
                    StringUtils.compareCaseInsensitive(
                        cell.column.attrName,
                        this._focusAttribute,
                    ),
            );
            if (cell) {
                cell.focus();
                cellFocused = true;
            }
            this._focusAttribute = null;
        }

        if (!cellFocused) {
        // If no cell was focused, focus the first editable cell.
            this.dataCells
                .find((cell) => cell.column.visible && cell.column.isEditable)
                ?.focus();
        }
    }

    private _toEdit(value: boolean): void {
        if (this.checkAndLogDisposedUsage("_toEdit")) return;

        if (!this._dataCells.some(item => item.column.isEditable)) {
            for (const cell of this.dataCells) {
                if (!cell.column.visible) continue;
                const newCell = cell.render();
                if (newCell) //sloupec mohl změnit viditelnost a render vrátil null.
                    this.appendChild(newCell);
            }
            return; // pokud neni zadny sloupec editovatelny netreba nic provadet
        }

        this._cleanRowElement();

        const frozenRowHelper = createElement({ classes: "frozen-part-helper" });
        this._frozenPart = createElement({ classes: "frozen-part" });
        frozenRowHelper.appendChild(this._frozenPart);
        this.insertAdjacentElement("afterbegin", frozenRowHelper);

        this.appendChild(frozenRowHelper);

        this._frozenPart.appendChild(this._createFirstCell());

        // odregistrace události na zmenu s predchozího radku
        this._eventsController.removeByKey(cellInEditListenerKey);

        this.dataCells.forEach(cell => {
            if (!cell.column.visible) {
                return;
            }

            // pokud neni editovatelny sloupec, je zbytecne cokoli vytvaret
            if (!cell.column.isEditable) {
                const newCell = cell.render();
                if (newCell) { //sloupec mohl změnit viditelnost a render vrátil null.
                    if (cell.column.isFrozen) {
                        this._frozenPart.appendChild(newCell);
                    } else {
                        this.appendChild(newCell);
                    }
                }
                return;
            }

            cell.toEdit(value);

            if (value) {
                this._eventsController.addListenerWithKey(cellInEditListenerKey, cell.element, "valuechanged", (e: CustomEvent) => {
                    cell.addPendingActions(this._cellValueChanged(cell, e.detail.value));
                });
                this._eventsController.addListenerWithKey(cellInEditListenerKey, cell.element, "keydown", (e: KeyboardEvent) => {
                    if (e.ctrlKey && e.code === Code.KeyA) { // Ctrl + A. Aby nedošlo k vybrání všech záznamů v seznamu (DataGrid.ts)
                        EventHelper.stopPropagation(e);
                    }
                    if (ROW_SELECTION_KEY_CODES.includes(e.code)) {
                        if (cell.isChanged()) {
                            this.isChanged = true;
                        }
                    }
                });
            }

            const newCell = cell.render();
            if (!newCell) { //sloupec mohl změnit viditelnost a render vrátil null.
                return;
            }

            if (cell.column.isFrozen) {
                this._frozenPart.appendChild(newCell);
            } else {
                this.appendChild(newCell);
            }
        });

        if (value) {
            this._focusCell();
        } else {
            if (document.activeElement === document.body) {
                // Nastavíme zaměření na dataGrid, jen pokud už není někde jinde.
                this._dataGrid.focus();
            }
        }

        if (this.dataCells.some(cell => cell.column.visible)) {
            this.classList.toggle("InEdit", value);
        }
    }

    /**
     * Udalost pri doubleclicku, na editovatelnem seznamu prejde do editace a preda focus bunce, na kterou se kliklo (pokud je editovatelna), jinak na prvni editovatelnou bunku v radku
     */
    protected override _doubleClickEvt(): void {
        // Prazdna metoda
    }

    /**
     * Renders the control to the specified HTML writer.
     */
    public override render(): HTMLElement {
        if (this.checkAndLogDisposedUsage("render")) return undefined;

        this._cleanRowElement();
        const frozenRowHelper = createElement({ classes: "frozen-part-helper" });
        this._frozenPart = createElement({ classes: "frozen-part" });
        frozenRowHelper.appendChild(this._frozenPart);
        this.appendChild(frozenRowHelper);

        this._frozenPart.appendChild(this._createFirstCell());

        this._dataCells.forEach((cell) => {
            if (cell.column.visible) {
                cell.toEdit(this.isInEdit);
                const output = cell.render();
                if (output) { //sloupec mohl změnit viditelnost a render vrátil null.
                    if (cell.column.isFrozen) {
                        this._frozenPart.appendChild(output);
                    } else {
                        this.appendChild(output);
                    }
                }
            }
        });

        return this as HTMLElement;
    }

    /**
     * Vybere řádek
     * @param isFirstSelection Zda se jedná o úplně první výber nově vytvořeného řádku
     */
    private _select(isFirstSelection: boolean = false): void {
        if (this.checkAndLogDisposedUsage("_select")) {
            return;
        }

        const oldSel = this.isSelected;
        //const oldEdit = this.DataGrid.LastClickedRow.IsEdited;

        this._dataGrid.focus();
        this._dataGrid.isSelectedAll = false;
        this._dataGrid.clearSelectedRows();
        this.isSelected = true;

        const lastClickedRow = this._dataGrid.lastClickedRow;
        if (lastClickedRow && !lastClickedRow.isChanged) {
            lastClickedRow.isInEdit = false;
        }
        this._dataGrid.lastClickedRow = this;
        //this.IsEdited = oldEdit;

        if (!oldSel) {
            this._dispatchSelectionChangeEvent(this.dataKey, { doubleClick: false, clickedRow: this, newRowFirstSelect: isFirstSelection });
        }
    }

    public override select(isFirstSelection: boolean = false): void {
        if (this.checkAndLogDisposedUsage("select")) {
            return;
        }

        this._select(isFirstSelection);
    }

    //#endregion
}

registerCustomElement("tsc-EditableDataRow", EditableDataRow);
